import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ViewEncapsulation, OnInit, OnDestroy } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { RendererSelectorComponent } from '../../renderer-selector.component';
import { BaseCrmModelDrivenFormComponent } from './base-model-driven-form.component';
import { CrmFormButtonGroupComponent } from './form-button-group.component';
import { CrmFormLoadingIndicatorComponent } from './form-loading-indicator.component';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { map, take } from 'rxjs/operators';
import { Observable } from 'rxjs';

@Component({
  selector: 'crm-panel-driven-form',
  templateUrl: './panel-driven-form.component.html',
  styleUrls: ['./base-model-driven-form.component.scss', './panel-driven-form.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    RendererSelectorComponent,
    CrmFormLoadingIndicatorComponent,
    CrmFormButtonGroupComponent,
    MatDividerModule,
    MatExpansionModule,
  ],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CrmPanelDrivenFormComponent extends BaseCrmModelDrivenFormComponent implements OnInit, OnDestroy {
  private readonly STORAGE_KEY = 'crm-panel-form-expanded-state';
  private expandedPanels: string[] = [];
  private currentObjectSubtype = this.objectSubtype;

  ngOnInit(): void {
    this.loadExpandedState();
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.saveExpandedState();
  }

  onPanelOpened(description: string): void {
    if (!this.expandedPanels.includes(description)) {
      this.expandedPanels.push(description);
      this.saveExpandedState();
    }
  }

  onPanelClosed(description: string): void {
    this.expandedPanels = this.expandedPanels.filter((panel) => panel !== description);
    this.saveExpandedState();
  }

  isPanelExpanded(description: string): boolean {
    return this.expandedPanels.includes(description);
  }

  private getStorageKey(): Observable<string> {
    return this.config.namespace$.pipe(
      map((namespace) => {
        if (namespace) {
          if (this.objectType === 'CustomObject') {
            return `${namespace}-${this.STORAGE_KEY}-${this.objectType}-${this.currentObjectSubtype}`;
          } else {
            return `${namespace}-${this.STORAGE_KEY}-${this.objectType}`;
          }
        }
        return this.STORAGE_KEY;
      }),
    );
  }

  private saveExpandedState(): void {
    try {
      this.getStorageKey()
        .pipe(take(1))
        .subscribe((storageKey: string) => {
          if (!storageKey) {
            console.warn('Storage key is empty, not saving expanded state.');
            return;
          }
          localStorage.setItem(storageKey, JSON.stringify(this.expandedPanels));
        });
    } catch (error) {
      console.warn('Failed to save panel expanded state to localStorage:', error);
    }
  }

  private loadExpandedState(): void {
    try {
      this.getStorageKey()
        .pipe(take(1))
        .subscribe((storageKey: string) => {
          if (!storageKey) {
            console.warn('Storage key is empty.');
            return;
          }
          const savedState = localStorage.getItem(storageKey);
          if (savedState) {
            this.expandedPanels = JSON.parse(savedState) as string[];
          }
        });
    } catch (error) {
      console.warn('Failed to load panel expanded state from localStorage:', error);
    }
  }
}
